import { useEffect, useId, useMemo, useState } from "react";
import { CalendarClock } from "lucide-react";
import { Button } from "../ui/Button";
import { DatePicker } from "../ui/DatePicker";
import { parseDateValue, toDateValue } from "../ui/DateCalendar";
import { DayOfMonthPicker } from "../ui/DayOfMonthPicker";
import { DayOfWeekPicker } from "../ui/DayOfWeekPicker";
import { Field, FieldHint, FieldLabel } from "../ui/Field";
import { Modal } from "../ui/Modal";
import { ScheduleTimeField } from "../ui/ScheduleTimeField";
import { SegmentedControl } from "../ui/SegmentedControl";
import {
  computeNextMonthDayDate,
  computeNextRunDate,
  computeNextWeekdayDate,
  dateFromScheduleFields,
  formatDraftSchedulePreview,
  formatScheduleCardLabel,
  formatTimezoneShort,
  getBrowserTimezone,
  getDefaultScheduleDay,
  normalizeScheduleRecurrence,
  normalizeScheduleTimeInput,
  SCHEDULE_RECURRENCES,
  scheduleFieldsFromDate,
  syncScheduleDateForRecurrence,
} from "../../utils/scheduleUtils";

function toDateInputValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return toDateValue(date);
}

function toTimeInputValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "09:00";
  return normalizeScheduleTimeInput(`${date.getHours()}:${date.getMinutes()}`);
}

const recurrenceOptions = SCHEDULE_RECURRENCES.map(({ value, label }) => ({
  value,
  label,
  tabLabel:
    value === "weekly"
      ? "Week"
      : value === "daily"
        ? "Day"
        : value === "monthly"
          ? "Month"
          : "Once",
}));

function getDayOfWeekFromDate(dateValue) {
  return parseDateValue(dateValue)?.getDay() ?? getDefaultScheduleDay();
}

function getDayOfMonthFromDate(dateValue) {
  return parseDateValue(dateValue)?.getDate() ?? new Date().getDate();
}

function ScheduleFields({
  dateValue,
  timeValue,
  recurrence,
  timezone,
  disabled,
  saving,
  mode = "inline",
  onUpdate,
}) {
  const normalizedRecurrence = normalizeScheduleRecurrence(recurrence);
  const dayOfWeek = getDayOfWeekFromDate(dateValue);
  const dayOfMonth = getDayOfMonthFromDate(dateValue);
  const isModal = mode === "modal";

  const patch = (updates) => {
    const nextRecurrence = normalizeScheduleRecurrence(
      updates.recurrence ?? normalizedRecurrence,
    );
    const nextTime = updates.timeValue ?? timeValue;
    let nextDate = updates.dateValue ?? dateValue;

    if (updates.recurrence !== undefined && updates.dateValue === undefined) {
      nextDate = syncScheduleDateForRecurrence(
        nextRecurrence,
        nextTime,
        dateValue,
      );
    } else if (
      updates.timeValue !== undefined &&
      updates.dateValue === undefined &&
      nextRecurrence !== "once"
    ) {
      nextDate = syncScheduleDateForRecurrence(
        nextRecurrence,
        nextTime,
        dateValue,
      );
    }

    onUpdate?.({
      dateValue: nextDate,
      timeValue: nextTime,
      recurrence: nextRecurrence,
    });
  };

  const timeField = (
    <ScheduleTimeField
      value={timeValue}
      disabled={disabled || saving}
      layout={isModal ? "row" : "stacked"}
      onChange={(value) => patch({ timeValue: value })}
    />
  );

  if (isModal) {
    return (
      <div className="space-y-5">
        <Field>
          <FieldLabel>How often</FieldLabel>
          <SegmentedControl
            value={normalizedRecurrence}
            options={recurrenceOptions}
            disabled={disabled || saving}
            onChange={(value) => patch({ recurrence: value })}
            fullWidth
            variant="brand"
            size="sm"
            ariaLabel="Repeat schedule"
          />
        </Field>

        {normalizedRecurrence === "weekly" ? (
          <Field>
            <FieldLabel>On which day?</FieldLabel>
            <DayOfWeekPicker
              value={dayOfWeek}
              disabled={disabled || saving}
              onChange={(nextDay) =>
                patch({
                  dateValue: toDateValue(
                    computeNextWeekdayDate(nextDay, timeValue),
                  ),
                })
              }
            />
          </Field>
        ) : null}

        {normalizedRecurrence === "monthly" ? (
          <Field>
            <FieldLabel>On which day of the month?</FieldLabel>
            <DayOfMonthPicker
              value={dayOfMonth}
              disabled={disabled || saving}
              variant="picker"
              onChange={(nextDay) =>
                patch({
                  dateValue: toDateValue(
                    computeNextMonthDayDate(nextDay, timeValue),
                  ),
                })
              }
            />
            <FieldHint>Months with fewer days use the last day.</FieldHint>
          </Field>
        ) : null}

        {normalizedRecurrence === "once" ? (
          <Field>
            <FieldLabel>On which date?</FieldLabel>
            <DatePicker
              value={dateValue}
              disabled={disabled || saving}
              minDate={new Date()}
              onChange={(value) => patch({ dateValue: value })}
            />
          </Field>
        ) : null}

        <Field>
          <FieldLabel>At what time?</FieldLabel>
          {timeField}
        </Field>

        {saving ? <FieldHint>Saving schedule…</FieldHint> : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-brand/20 bg-brand/5 px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brand">
          Preview
        </p>
        <p className="mt-1 text-sm leading-relaxed text-foreground">
          {formatDraftSchedulePreview({
            recurrence: normalizedRecurrence,
            timeValue,
            dateValue,
            dayOfWeek,
            timezone,
          })}
        </p>
      </div>

      <Field>
        <FieldLabel>How often</FieldLabel>
        <SegmentedControl
          value={normalizedRecurrence}
          options={recurrenceOptions}
          disabled={disabled || saving}
          onChange={(value) => patch({ recurrence: value })}
          fullWidth
          variant="brand"
          size="sm"
          ariaLabel="Repeat schedule"
        />
      </Field>

      {normalizedRecurrence === "weekly" ? (
        <Field>
          <FieldLabel>Day of week</FieldLabel>
          <DayOfWeekPicker
            value={dayOfWeek}
            disabled={disabled || saving}
            onChange={(nextDay) =>
              patch({
                dateValue: toDateValue(
                  computeNextWeekdayDate(nextDay, timeValue),
                ),
              })
            }
          />
        </Field>
      ) : null}

      {normalizedRecurrence === "monthly" ? (
        <Field>
          <FieldLabel>Day of month</FieldLabel>
          <DayOfMonthPicker
            value={dayOfMonth}
            disabled={disabled || saving}
            onChange={(nextDay) =>
              patch({
                dateValue: toDateValue(
                  computeNextMonthDayDate(nextDay, timeValue),
                ),
              })
            }
          />
          <FieldHint>
            If a month has fewer days, the last day of that month is used.
          </FieldHint>
        </Field>
      ) : null}

      {normalizedRecurrence === "once" ? (
        <Field>
          <FieldLabel>Run on</FieldLabel>
          <DatePicker
            value={dateValue}
            disabled={disabled || saving}
            minDate={new Date()}
            onChange={(value) => patch({ dateValue: value })}
          />
        </Field>
      ) : null}

      <Field>
        <FieldLabel>Time</FieldLabel>
        {timeField}
      </Field>

      {saving ? <FieldHint>Saving schedule…</FieldHint> : null}
    </div>
  );
}

function ScheduleModalHeader(context) {
  if (!context?.siteLabel && !context?.sheetName) {
    return { eyebrow: null, title: "Edit schedule", description: null };
  }

  const title = context.siteUrl ? (
    <a
      href={context.siteUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand hover:underline"
    >
      {context.siteLabel}
    </a>
  ) : (
    context.siteLabel
  );

  const description = context.sheetName ? (
    <>
      {context.sheetId ? (
        <a
          href={`https://docs.google.com/spreadsheets/d/${context.sheetId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          {context.sheetName}
        </a>
      ) : (
        context.sheetName
      )}
      {context.tabName ? ` · ${context.tabName}` : ""}
    </>
  ) : null;

  return {
    eyebrow: "Monitoring schedule",
    title,
    description,
  };
}

export function WeeklySchedulePicker({
  dayOfWeek = getDefaultScheduleDay(),
  time = "09:00",
  timezone = getBrowserTimezone(),
  recurrence = "weekly",
  nextScheduledAt,
  disabled = false,
  saving = false,
  variant = "modal",
  context,
  onSave,
}) {
  const resolvedTimezone =
    timezone && timezone !== "UTC" ? timezone : getBrowserTimezone();
  const resolvedRecurrence = normalizeScheduleRecurrence(recurrence);
  const initialDate = dateFromScheduleFields({
    dayOfWeek,
    time,
    nextScheduledAt,
  });

  const [open, setOpen] = useState(false);
  const [draftDateValue, setDraftDateValue] = useState(
    toDateInputValue(initialDate),
  );
  const [draftTimeValue, setDraftTimeValue] = useState(
    toTimeInputValue(initialDate),
  );
  const [draftRecurrence, setDraftRecurrence] = useState(resolvedRecurrence);
  const modalTitleId = useId();

  const syncDraftFromProps = () => {
    const next = dateFromScheduleFields({
      dayOfWeek,
      time,
      nextScheduledAt,
    });
    setDraftDateValue(toDateInputValue(next));
    setDraftTimeValue(toTimeInputValue(next));
    setDraftRecurrence(normalizeScheduleRecurrence(recurrence));
  };

  useEffect(() => {
    if (!open) syncDraftFromProps();
  }, [open, dayOfWeek, time, recurrence, nextScheduledAt]);

  const buildSavePayload = (dateValue, timeValue, recurrenceValue) => {
    const picked = computeNextRunDate({
      recurrence: recurrenceValue,
      time: timeValue,
      dayOfWeek: getDayOfWeekFromDate(dateValue),
      dateValue,
    });
    if (!picked || Number.isNaN(picked.getTime())) return null;
    return {
      ...scheduleFieldsFromDate(picked, resolvedTimezone, recurrenceValue),
      nextScheduledAt: picked.toISOString(),
    };
  };

  const applyDraft = () => {
    const payload = buildSavePayload(
      draftDateValue,
      draftTimeValue,
      draftRecurrence,
    );
    if (!payload) return;
    onSave?.(payload);
    setOpen(false);
  };

  const emitInlineSave = (dateValue, timeValue, recurrenceValue) => {
    const payload = buildSavePayload(dateValue, timeValue, recurrenceValue);
    if (payload) onSave?.(payload);
  };

  const modalFooter = useMemo(() => {
    const scheduleLabel = formatScheduleCardLabel({
      recurrence: draftRecurrence,
      dayOfWeek: getDayOfWeekFromDate(draftDateValue),
      timeStr: draftTimeValue,
      nextScheduledAt: draftDateValue
        ? `${draftDateValue}T${draftTimeValue}:00`
        : undefined,
    });
    const nextRun = computeNextRunDate({
      recurrence: draftRecurrence,
      time: draftTimeValue,
      dayOfWeek: getDayOfWeekFromDate(draftDateValue),
      dateValue: draftDateValue,
    });
    const nextLabel =
      nextRun && !Number.isNaN(nextRun.getTime())
        ? nextRun.toLocaleString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        : null;

    return {
      scheduleLabel,
      nextLabel,
      timezone: formatTimezoneShort(resolvedTimezone),
    };
  }, [draftRecurrence, draftTimeValue, draftDateValue, resolvedTimezone]);

  if (variant === "inline") {
    return (
      <ScheduleFields
        dateValue={draftDateValue}
        timeValue={draftTimeValue}
        recurrence={draftRecurrence}
        timezone={resolvedTimezone}
        disabled={disabled}
        saving={saving}
        mode="inline"
        onUpdate={({ dateValue, timeValue, recurrence: nextRecurrence }) => {
          setDraftDateValue(dateValue);
          setDraftTimeValue(timeValue);
          setDraftRecurrence(nextRecurrence);
          emitInlineSave(dateValue, timeValue, nextRecurrence);
        }}
      />
    );
  }

  const modalHeader = ScheduleModalHeader(context);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={modalTitleId}
        onClick={() => !disabled && setOpen(true)}
        className="w-full shrink-0 sm:w-auto"
      >
        <CalendarClock size={14} />
        Edit schedule
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        titleId={modalTitleId}
        panelClassName="max-w-md"
        eyebrow={modalHeader.eyebrow}
        title={modalHeader.title}
        description={modalHeader.description}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {modalFooter.scheduleLabel}
              </p>
              {modalFooter.nextLabel ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Next run {modalFooter.nextLabel} · {modalFooter.timezone}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="brand"
                size="sm"
                disabled={disabled || saving || !draftDateValue}
                onClick={applyDraft}
              >
                {saving ? "Saving…" : "Save schedule"}
              </Button>
            </div>
          </div>
        }
      >
        <ScheduleFields
          dateValue={draftDateValue}
          timeValue={draftTimeValue}
          recurrence={draftRecurrence}
          timezone={resolvedTimezone}
          disabled={disabled}
          saving={saving}
          mode="modal"
          onUpdate={({ dateValue, timeValue, recurrence: nextRecurrence }) => {
            setDraftDateValue(dateValue);
            setDraftTimeValue(timeValue);
            setDraftRecurrence(nextRecurrence);
          }}
        />
      </Modal>
    </>
  );
}
